import React, { useEffect, Fragment, useState } from "react";
import { closeModal } from "../../store/modal/modalReducer";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faTimes, faPaperPlane, faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";
import { Dialog, Transition, Listbox } from "@headlessui/react";
import { Formik, Form, Field } from "formik";
import ErrorBoundary from "../../containers/ErrorBoundary";

import moment from "moment";
import NotificationToast from "../Helpers/NotificationToast";
import SubmitButton from "../Forms/SubmitButton";
import SettingsDateTimePicker from "../Forms/SettingsDateTimePicker";
import { getBooking } from "../../store/booking/bookingActions";
import { getStaff } from "../../store/auth/authActions";
import ProfilePicture from "../Auth/ProfilePicture";
import SettingsSwitch from "../Forms/SettingsSwitch";

export default function ModalMoveFiles(props) {
  const dispatch = useDispatch();
  const [open] = useState(true);
  const { staff } = useSelector((state) => state.auth);
  const current_booking = useSelector((state) => state.booking.current_booking);
  
  // Send functionality state
  const [selectedImages, setSelectedImages] = useState([]);
  const [showSendInterface, setShowSendInterface] = useState(false);
  const [message, setMessage] = useState('Here are the inspection images for your review.');
  const [sendMethod, setSendMethod] = useState('auto');
  const [sending, setSending] = useState(false);

  const toggleImageSelection = (filePath) => {
    setSelectedImages(prev => 
      prev.includes(filePath) 
        ? prev.filter(path => path !== filePath)
        : [...prev, filePath]
    );
  };

  const selectAllImages = () => {
    if (selectedImages.length === props.files.length) {
      setSelectedImages([]);
    } else {
      setSelectedImages(props.files.map(file => file.path));
    }
  };

  const handleSendImages = async () => {
    if (selectedImages.length === 0) {
      toast.error('Please select at least one image to send');
      return;
    }

    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSending(true);

    try {
      // Determine inspection type from file paths
      const inspectionType = selectedImages[0].includes('/in_inspections/') ? 'in' : 'out';

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}inspections/send-legacy-images`,
        {
          booking_id: current_booking.id,
          file_paths: selectedImages,
          message: message.trim(),
          send_method: sendMethod,
          inspection_type: inspectionType
        }
      );

      if (response.data.success) {
        toast.success(
          `Successfully sent ${response.data.image_count} image${response.data.image_count !== 1 ? 's' : ''} via ${response.data.send_method}`
        );
        
        // Reset state
        setSelectedImages([]);
        setShowSendInterface(false);
        setMessage('Here are the inspection images for your review.');
      } else {
        toast.error(response.data.message || 'Failed to send images');
      }
    } catch (error) {
      console.error('Error sending images:', error);
      const errorMessage = error.response?.data?.message || 'Failed to send images. Please try again.';
      toast.error(errorMessage);
    } finally {
      setSending(false);
    }
  };

  async function download_file(file) {
    await axios({
      method: "post",
      responseType: "blob",
      url: `${process.env.REACT_APP_API_URL}download_file_link`,
      data: { file_link: file },
    })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        let name = file.split("/").pop();
        link.setAttribute("download", name);
        document.body.appendChild(link);
        link.click();
      })
      .catch(function (error) {
        toast(
          <NotificationToast
            title="File Missing"
            message={"Something went wrong, we could not download this file!"}
            type="error"
          />
        );
      });
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed z-50 inset-0 overflow-y-auto"
        open={open}
        onClose={() => dispatch(closeModal())}
      >
        <div className="flex items-center justify-center min-h-screen px-4 py-6">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <ErrorBoundary>
              <div className="relative bg-white rounded-lg shadow-xl transform transition-all w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-6">
                {/* Header */}
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Inspection Images</h3>
                  <button
                    type="button"
                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-0"
                    onClick={() => dispatch(closeModal())}
                  >
                    <span className="sr-only">Close</span>
                    <FontAwesomeIcon
                      className="h-6 w-6"
                      aria-hidden="true"
                      icon={faTimes}
                    />
                  </button>
                </div>

                {/* Send to Tenant Button */}
                {props.files && props.files.length > 0 && (
                  <button
                    onClick={() => setShowSendInterface(!showSendInterface)}
                    className="w-full flex items-center justify-between px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors mb-4"
                    disabled={sending}
                  >
                    <span className="flex items-center gap-2">
                      <FontAwesomeIcon icon={faPaperPlane} />
                      Send to Tenant
                    </span>
                    <FontAwesomeIcon icon={showSendInterface ? faChevronUp : faChevronDown} />
                  </button>
                )}

                {/* Send Interface */}
                {showSendInterface && (
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded">
                    <div className="space-y-3">
                      {/* Selection Info */}
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-700">
                          Selected: {selectedImages.length} of {props.files.length} images
                        </p>
                        <button
                          onClick={selectAllImages}
                          className="text-xs px-2 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50"
                          disabled={sending}
                        >
                          {selectedImages.length === props.files.length ? 'Deselect All' : 'Select All'}
                        </button>
                      </div>

                      {/* Message Input */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Message
                        </label>
                        <textarea
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          rows="3"
                          placeholder="Enter your message to the tenant..."
                          disabled={sending}
                          maxLength={2000}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {message.length}/2000 characters
                        </p>
                      </div>

                      {/* Send Method */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Send via
                        </label>
                        <div className="flex gap-4">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              value="auto"
                              checked={sendMethod === 'auto'}
                              onChange={(e) => setSendMethod(e.target.value)}
                              className="mr-2"
                              disabled={sending}
                            />
                            <span className="text-sm">Auto (WhatsApp → Email)</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              value="whatsapp"
                              checked={sendMethod === 'whatsapp'}
                              onChange={(e) => setSendMethod(e.target.value)}
                              className="mr-2"
                              disabled={sending}
                            />
                            <span className="text-sm">WhatsApp</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              value="email"
                              checked={sendMethod === 'email'}
                              onChange={(e) => setSendMethod(e.target.value)}
                              className="mr-2"
                              disabled={sending}
                            />
                            <span className="text-sm">Email</span>
                          </label>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={handleSendImages}
                          disabled={sending || selectedImages.length === 0}
                          className={`flex-1 px-4 py-2 rounded font-medium transition-colors ${
                            sending || selectedImages.length === 0
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : 'bg-green-600 text-white hover:bg-green-700'
                          }`}
                        >
                          {sending ? 'Sending...' : `Send Images (${selectedImages.length})`}
                        </button>
                        <button
                          onClick={() => {
                            setShowSendInterface(false);
                            setSelectedImages([]);
                          }}
                          disabled={sending}
                          className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Images Grid */}
                <div className="flex-1 overflow-y-auto">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {props.files &&
                      props.files.map((file, index) => {
                        const isSelected = selectedImages.includes(file.path);
                        return (
                          <div
                            key={index}
                            className={`border rounded-lg overflow-hidden ${
                              isSelected ? 'ring-2 ring-blue-500 border-blue-500' : ''
                            }`}
                          >
                            <div className="relative">
                              {/* Selection Checkbox */}
                              {showSendInterface && (
                                <div 
                                  className="absolute top-2 left-2 z-10 bg-white rounded-md p-1 shadow-md cursor-pointer hover:bg-gray-50"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!sending) {
                                      toggleImageSelection(file.path);
                                    }
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => {}}
                                    className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer pointer-events-none"
                                    disabled={sending}
                                  />
                                </div>
                              )}
                              
                              {/* Download Button */}
                              <div className="absolute top-2 right-2 z-10">
                                <FontAwesomeIcon
                                  icon={faDownload}
                                  onClick={() => download_file(file.path)}
                                  className="text-white bg-gray-800 bg-opacity-50 hover:bg-opacity-75 p-2 rounded cursor-pointer"
                                  title="Download File"
                                />
                              </div>
                              
                              {/* Image */}
                              <img
                                src={`data:image/jpg;base64,${file.content}`}
                                alt="inspection image"
                                className="w-full h-64 object-cover cursor-pointer"
                                onClick={() => window.open(`data:image/jpg;base64,${file.content}`, '_blank')}
                              />
                            </div>
                            
                            {/* File Name */}
                            <div className="p-2 bg-gray-50">
                              <p className="text-xs text-gray-600 truncate">
                                {file.path.split('/').pop()}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>
            </ErrorBoundary>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
